import { RouterModule, Routes } from '@angular/router';
import { NgModule } from '@angular/core';

import { ApplyApplicationModeGuard } from '@guards/apply-application-mode-guard.service';
import { NotFoundComponent } from './shared/components/not-found/not-found.component';
import { PageErrorComponent } from './shared/components/page-error/page-error.component';
import { AppLayoutComponent } from './modules/app-layout/pages/app-layout/app-layout.component';
import { enumUserRole } from './data-layer/enums/user';
import { RoleGuard } from '@guards/role.guard';
import { ExistCompanyGuard } from '@guards/exist-company.guard';
import { ExistOrganizationGuard } from '@guards/exist-organization.guard';

const user = { roles: [enumUserRole.USER] };
const support = { roles: [enumUserRole.SUPPORT, enumUserRole.ADMIN, enumUserRole.SUPER_ADMIN] };
const superAdmin = { roles: [enumUserRole.SUPER_ADMIN] };
const supportWithActiveUser = { roles: [enumUserRole.SUPPORT, enumUserRole.ADMIN, enumUserRole.SUPER_ADMIN], isOnlyActiveRole: false };

const APP_ROUTES: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'dashboard',
  },
  {
    path: 'auth',
    loadChildren: () => import('app/modules/auth/auth.module').then((m) => m.AuthModule),
  },
  {
    path: 'legal-terms',
    loadChildren: () => import('app/modules/legal-terms/legal-terms.module').then((m) => m.LegalTermsModule),
  },
  {
    path: 'privacy-policy',
    loadChildren: () => import('app/modules/privacy-policy/privacy-policy.module').then((m) => m.PrivacyPolicyModule),
  },
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [ApplyApplicationModeGuard],
    // canActivateChild: [CanActivatePendingReqGuard], #5683 investigate weak redirects with pending requests
    children: [
      {
        path: 'dashboard',
        loadChildren: () => import('app/modules/dashboard/dashboard.module').then((m) => m.DashboardModule),
        data: user,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'loans',
        loadChildren: () => import('app/modules/loans/loans.module').then((m) => m.LoansModule),
        data: supportWithActiveUser,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'dashboard-management',
        loadChildren: () => import('app/modules/dashboard-management/dashboard-management.module').then((m) => m.DashboardManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'on-boarding',
        loadChildren: () => import('app/modules/self-filer-request/self-filer-request.module').then((m) => m.SelfFilerRequestModule),
        data: user,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'prelimetary-statement',
        loadChildren: () =>
          import('app/modules/preliminary-statement/preliminary-statement.module').then((m) => m.PreliminaryStatementModule),
        data: user,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'isf',
        loadChildren: () => import('app/modules/isf/isf.module').then((m) => m.IsfModule),
        data: user,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'isfs-management',
        loadChildren: () => import('@app/modules/isfs-management/isfs-management.module').then((m) => m.IsfsManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'bonds',
        loadChildren: () => import('app/modules/bonds/bonds.module').then((m) => m.BondsModule),
        data: user,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'bina-catalog',
        loadChildren: () => import('app/modules/bina-catalog/bina-catalog.module').then((m) => m.BinaCatalogModule),
        data: user,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'companies',
        loadChildren: () => import('app/modules/company/company.module').then((m) => m.CompanyModule),
        data: user,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'organizations-management',
        loadChildren: () =>
          import('@app/modules/organizations-management/organizations-management.module').then((m) => m.OrganizationsManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'companies-management',
        loadChildren: () =>
          import('@app/modules/companies-management/companies-management.module').then((m) => m.CompaniesManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'user',
        loadChildren: () => import('app/modules/user/user.module').then((m) => m.UserModule),
      },
      {
        path: 'users-management',
        loadChildren: () => import('@app/modules/users-management/users-management.module').then((m) => m.UsersManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'beta-feature-management',
        loadChildren: () =>
          import('@app/modules/beta-feature-management/beta-feature-management.module').then((m) => m.BetaFeatureManagementModule),
        data: superAdmin,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'lcl-shipping',
        loadChildren: () => import('app/modules/lcl-shipping/lcl-shipping.module').then((m) => m.LclShippingModule),
        data: user,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'entry-summary',
        loadChildren: () => import('app/modules/entry-summary/entry-summary.module').then((m) => m.EntrySummaryModule),
        data: user,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'entry-summaries-management',
        loadChildren: () =>
          import('@app/modules/entry-summaries-management/entry-summaries-management.module').then((m) => m.EntrySummariesManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'contact-support',
        loadChildren: () => import('app/modules/contact-support/contact-support.module').then((m) => m.ContactSupportModule),
        data: user,
        canActivate: [RoleGuard],
      },
      {
        path: 'register',
        loadChildren: () => import('app/modules/register/register.module').then((m) => m.RegisterModule),
        canActivate: [RoleGuard, ExistCompanyGuard],
        data: user,
      },
      {
        path: 'bills-management',
        loadChildren: () => import('@app/modules/bills-management/bills-management.module').then((m) => m.BillsManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'price-list-combined-management',
        loadChildren: () =>
          import('@app/modules/management/price-list-combined-management/price-list-combined-management.module').then(
            (m) => m.PriceListCombinedManagementModule,
          ),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'bond-requests-management',
        loadChildren: () =>
          import('@app/modules/bond-requests-management/bond-requests-management.module').then((m) => m.BondRequestsManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'digital-poa-management',
        loadChildren: () =>
          import('@app/modules/digital-poa-management/digital-poa-management.module').then((m) => m.DigitalPoaManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'bonds-management',
        loadChildren: () => import('@app/modules/bonds-management/bonds-management.module').then((m) => m.BondsManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'document-management',
        loadChildren: () => import('@app/modules/document-management/document-management.module').then((m) => m.DocumentManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'statements-management',
        loadChildren: () =>
          import('@app/modules/statements-management/statements-management.module').then((m) => m.StatementsManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'lines-management',
        loadChildren: () => import('@app/modules/lines-management/lines-management.module').then((m) => m.LinesManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'lds-connection-management',
        loadChildren: () =>
          import('@app/modules/lds-connection-management/lds-connection-management.module').then((m) => m.LdsConnectionManagementModule),
        data: superAdmin,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'routes-management',
        loadChildren: () => import('@app/modules/routes-management/routes-management.module').then((m) => m.RoutesManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'voyages-management',
        loadChildren: () =>
          import('@app/modules/management/voyages-management/voyage-management.module').then((m) => m.VoyageManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'container-freight-storages-management',
        loadChildren: () =>
          import('@app/modules/container-freight-storages-management/container-freight-storages-management.module').then(
            (m) => m.ContainerFreightStoragesManagementModule,
          ),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'containers-management',
        loadChildren: () =>
          import('@app/modules/containers-management/containers-management.module').then((m) => m.ContainersManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'container-types-management',
        loadChildren: () =>
          import('@app/modules/container-types-management/container-types-management.module').then((m) => m.ContainerTypesManagementModule),
        data: superAdmin,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'container-seals-management',
        loadChildren: () =>
          import('@app/modules/container-seals-management/container-seals-management.module').then((m) => m.ContainerSealsManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'master-bills-management',
        loadChildren: () =>
          import('@app/modules/master-bills-management/master-bills-management.module').then((m) => m.MasterBillsManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'house-bills-management',
        loadChildren: () =>
          import('@app/modules/house-bills-management/house-bills-management.module').then((m) => m.HouseBillsManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'packing-lists-management',
        loadChildren: () =>
          import('@app/modules/packing-lists-management/packing-lists-management.module').then((m) => m.PackingListsManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'quotes-management',
        loadChildren: () => import('@app/modules/quotes-management/quotes-management.module').then((m) => m.QuotesManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'sales-taxes-management',
        loadChildren: () =>
          import('@app/modules/sales-taxes-management/sales-taxes-management.module').then((m) => m.SalesTaxesManagementModule),
        data: superAdmin,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'query-bl-management',
        loadChildren: () => import('@app/modules/query-bl-management/query-bl-management.module').then((m) => m.QueryBlManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'hints-management',
        loadChildren: () => import('@app/modules/hints-management/hints-management.module').then((m) => m.HintsManagementModule),
        data: superAdmin,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'products',
        loadChildren: () => import('@app/modules/products/products.module').then((m) => m.ProductsModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'product-details',
        loadChildren: () => import('@app/modules/product-details/product-details.module').then((m) => m.ProductDetailsModule),
        data: support,
        canActivate: [RoleGuard],
      },
      {
        path: 'brokers-management',
        loadChildren: () => import('@app/modules/brokers-management/brokers-management.module').then((m) => m.BrokersManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'affiliates-management',
        loadChildren: () =>
          import('@app/modules/affiliate-management/affiliate-management.module').then((m) => m.AffiliateManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'distribution-management',
        loadChildren: () =>
          import('@app/modules/distribution-management/distribution-management.module').then((m) => m.DistributionManagementModule),
        data: superAdmin,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'distribution-group-log',
        loadChildren: () =>
          import('@app/modules/distribution-group-log/distribution-group-log.module').then((m) => m.DistributionGroupLogModule),
        data: superAdmin,
        canActivate: [RoleGuard],
      },
      {
        path: 'contacts-management',
        loadChildren: () => import('@app/modules/contacts-management/contacts-management.module').then((m) => m.ContactsManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'coupons-management',
        loadChildren: () => import('@app/modules/coupons-management/coupons-management.module').then((m) => m.CouponsManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'agents-management',
        loadChildren: () => import('@app/modules/agents-management/agents-management.module').then((m) => m.AgentsManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'bill-items-management',
        loadChildren: () =>
          import('@app/modules/bill-items-management/bill-items-management.module').then((m) => m.BillItemsManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'additional-payments-management',
        loadChildren: () =>
          import('@app/modules/additional-payments-management/additional-payments-management.module').then(
            (m) => m.AdditionalPaymentsManagementModule,
          ),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'shipments-management',
        loadChildren: () =>
          import('@app/modules/management/shipment-management/shipment-management.module').then((m) => m.ShipmentManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'marine-insurance',
        loadChildren: () => import('@app/modules/marine-insurance/marine-insurance.module').then((m) => m.MarineInsuranceModule),
        data: user,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'marine-insurance-management',
        loadChildren: () =>
          import('@app/modules/management/marine-insurance-management/marine-insurance-management.module').then(
            (m) => m.MarineInsuranceManagementModule,
          ),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'payment',
        loadChildren: () => import('@app/modules/payment/payment.module').then((m) => m.PaymentModule),
        data: user,
        canActivate: [RoleGuard, ExistOrganizationGuard, ExistCompanyGuard],
      },
      {
        path: 'payment-methods-management',
        loadChildren: () =>
          import('@app/modules/payment-methods-management/payment-methods-management.module').then((m) => m.PaymentMethodsManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'alerts-management',
        loadChildren: () => import('@app/modules/alerts-management/alerts-management.module').then((m) => m.AlertsManagementModule),
        data: support,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'drip-capital',
        loadChildren: () => import('@app/modules/drip-capital/drip-capital.module').then((m) => m.DripCapitalModule),
        data: user,
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
      {
        path: 'customs-broker',
        loadChildren: () => import('@app/modules/customs-broker/customs-broker.module').then((m) => m.CustomsBrokerModule),
        data: {
          roles: [enumUserRole.CUSTOMS_BROKER, enumUserRole.SUPPORT, enumUserRole.ADMIN, enumUserRole.SUPER_ADMIN],
          isOnlyActiveRole: false,
        },
        canActivate: [RoleGuard, ExistCompanyGuard],
      },
    ],
  },
  {
    path: '',
    children: [
      { path: 'error', component: PageErrorComponent },
      { path: '**', component: NotFoundComponent },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(APP_ROUTES, { scrollPositionRestoration: 'enabled' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
